import { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

/**
 * Personel Raporları: dönem filtresi, sıralama, sütun filtreleri, çalışan
 * ayrıntısı ve Excel dökümü. Hesaplar tüm çalışanların kayıtları üzerinden
 * yapılır (useOperationsData yükler). Excel başlıkları Türkçe kalır.
 */
const usePersonnelStatistics = ({ allVisits, allCalendarJobs, employees, text }) => {
  // Personnel Reports Section State
  const [reportPeriod, setReportPeriod] = useState('month'); // 'week', 'month', 'year', 'custom'
  const [customReportStartDate, setCustomReportStartDate] = useState('');
  const [customReportEndDate, setCustomReportEndDate] = useState('');
  const [sortField, setSortField] = useState('totalVisits'); // Default sort by total visits
  const [sortDirection, setSortDirection] = useState('desc'); // 'asc' or 'desc'
  const [selectedEmployeeStats, setSelectedEmployeeStats] = useState(null);
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);

  // Column filter states for Personnel Reports table
  const [reportFilters, setReportFilters] = useState({
    employeeName: '',
    totalVisits: '',
    completedVisits: '',
    totalJobs: '',
    completedJobs: '',
    projectsWorkedOn: '',
    completionRate: ''
  });

  // Personnel Statistics Calculation
  const personnelStatistics = useMemo(() => {
    const now = new Date();
    let filterStartDate, filterEndDate;

    switch (reportPeriod) {
      case 'week':
        filterStartDate = new Date(now);
        filterStartDate.setDate(now.getDate() - now.getDay() + 1); // Monday
        filterEndDate = new Date(filterStartDate);
        filterEndDate.setDate(filterStartDate.getDate() + 6); // Sunday
        break;
      case 'month':
        filterStartDate = new Date(now.getFullYear(), now.getMonth(), 1);
        filterEndDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'year':
        filterStartDate = new Date(now.getFullYear(), 0, 1);
        filterEndDate = new Date(now.getFullYear(), 11, 31);
        break;
      case 'custom':
        filterStartDate = customReportStartDate ? new Date(customReportStartDate) : new Date(now.getFullYear(), now.getMonth(), 1);
        filterEndDate = customReportEndDate ? new Date(customReportEndDate) : new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      default:
        filterStartDate = new Date(now.getFullYear(), now.getMonth(), 1);
        filterEndDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    }

    filterStartDate.setHours(0, 0, 0, 0);
    filterEndDate.setHours(23, 59, 59, 999);

    const statsMap = new Map();

    // Initialize stats for all active employees
    employees.filter(emp => emp.Active === 1).forEach(emp => {
      statsMap.set(emp.id, {
        employeeId: emp.id,
        employeeName: `${emp.eName} ${emp.eSurname}`,
        totalVisits: 0,
        completedVisits: 0,
        inProgressVisits: 0,
        totalJobs: 0,
        completedJobs: 0,
        projectsWorkedOn: new Set(),
        totalVisitDays: 0,
        averageVisitDuration: 0,
        visitTypes: {}
      });
    });

    // Process visits (uses full dataset — lazy-loaded when Personnel Reports opens)
    allVisits.forEach(visit => {
      const visitDate = new Date(visit.date || visit.firstVisitDate);
      if (visitDate >= filterStartDate && visitDate <= filterEndDate) {
        const stats = statsMap.get(visit.employeeId);
        if (stats) {
          stats.totalVisits++;
          stats.totalVisitDays += (visit.period || 1);
          if (visit.status) {
            stats.completedVisits++;
          } else {
            stats.inProgressVisits++;
          }
          if (visit.projectId) {
            stats.projectsWorkedOn.add(visit.projectId);
          }
          // Track visit types
          const visitType = visit.visitType || 'Standart';
          stats.visitTypes[visitType] = (stats.visitTypes[visitType] || 0) + 1;
        }
      }
    });

    // Process calendar jobs (uses full dataset)
    allCalendarJobs.forEach(job => {
      const jobDate = new Date(job.startDate);
      if (jobDate >= filterStartDate && jobDate <= filterEndDate) {
        const stats = statsMap.get(job.employeeId);
        if (stats) {
          stats.totalJobs++;
          if (job.status) {
            stats.completedJobs++;
          }
        }
      }
    });

    // Convert to array and calculate averages
    const statsArray = Array.from(statsMap.values())
      .map(stat => ({
        ...stat,
        projectsWorkedOn: stat.projectsWorkedOn.size,
        completionRate: stat.totalVisits > 0 ? Math.round((stat.completedVisits / stat.totalVisits) * 100) : 0,
        averageVisitDuration: stat.totalVisits > 0 ? Math.round(stat.totalVisitDays / stat.totalVisits * 10) / 10 : 0
      }))
      .filter(stat => stat.totalVisits > 0 || stat.totalJobs > 0); // Only show employees with activity

    // Sort by selected field and direction
    const sortedArray = [...statsArray].sort((a, b) => {
      let aVal, bVal;
      switch (sortField) {
        case 'employeeName':
          aVal = a.employeeName.toLowerCase();
          bVal = b.employeeName.toLowerCase();
          break;
        case 'totalVisits':
          aVal = a.totalVisits;
          bVal = b.totalVisits;
          break;
        case 'completedVisits':
          aVal = a.completedVisits;
          bVal = b.completedVisits;
          break;
        case 'inProgressVisits':
          aVal = a.inProgressVisits;
          bVal = b.inProgressVisits;
          break;
        case 'projectsWorkedOn':
          aVal = a.projectsWorkedOn;
          bVal = b.projectsWorkedOn;
          break;
        case 'averageVisitDuration':
          aVal = a.averageVisitDuration;
          bVal = b.averageVisitDuration;
          break;
        case 'completionRate':
          aVal = a.completionRate;
          bVal = b.completionRate;
          break;
        case 'totalJobs':
          aVal = a.totalJobs;
          bVal = b.totalJobs;
          break;
        case 'completedJobs':
          aVal = a.completedJobs;
          bVal = b.completedJobs;
          break;
        default:
          aVal = a.totalVisits;
          bVal = b.totalVisits;
      }
      
      if (sortField === 'employeeName') {
        return sortDirection === 'asc' ? aVal.localeCompare(bVal, 'tr') : bVal.localeCompare(aVal, 'tr');
      }
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });

    // Calculate totals
    const totals = {
      totalVisits: statsArray.reduce((sum, s) => sum + s.totalVisits, 0),
      completedVisits: statsArray.reduce((sum, s) => sum + s.completedVisits, 0),
      inProgressVisits: statsArray.reduce((sum, s) => sum + s.inProgressVisits, 0),
      totalJobs: statsArray.reduce((sum, s) => sum + s.totalJobs, 0),
      completedJobs: statsArray.reduce((sum, s) => sum + s.completedJobs, 0),
      totalVisitDays: statsArray.reduce((sum, s) => sum + s.totalVisitDays, 0),
      uniqueProjects: new Set(statsArray.flatMap(s => [...(allVisits.filter(v => v.employeeId === s.employeeId).map(v => v.projectId))])).size,
      activeEmployees: statsArray.length
    };

    // Determine top performers
    const topByVisits = [...statsArray].sort((a, b) => b.totalVisits - a.totalVisits).slice(0, 3);
    const topByCompletion = [...statsArray].filter(s => s.totalVisits >= 3).sort((a, b) => b.completionRate - a.completionRate).slice(0, 3);

    return {
      employees: sortedArray,
      totals,
      topByVisits,
      topByCompletion,
      filterStartDate,
      filterEndDate
    };
  }, [allVisits, allCalendarJobs, employees, reportPeriod, customReportStartDate, customReportEndDate, sortField, sortDirection]);

  // Handle sort column click
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Handle report filter change
  const handleReportFilterChange = (field, value) => {
    setReportFilters(prev => ({ ...prev, [field]: value }));
  };

  // Filter personnel statistics based on column filters
  const filteredPersonnelEmployees = useMemo(() => {
    return personnelStatistics.employees.filter(stat => {
      // Employee name filter (text search)
      if (reportFilters.employeeName && 
          !stat.employeeName.toLowerCase().includes(reportFilters.employeeName.toLowerCase())) {
        return false;
      }
      // Numeric filters: support exact match, >, <, >=, <=
      const numericFields = ['totalVisits', 'completedVisits', 'totalJobs', 'completedJobs', 'projectsWorkedOn', 'completionRate'];
      for (const field of numericFields) {
        const filterVal = reportFilters[field].trim();
        if (!filterVal) continue;
        const statVal = stat[field];
        
        if (filterVal.startsWith('>=')) {
          const num = parseFloat(filterVal.slice(2));
          if (!isNaN(num) && statVal < num) return false;
        } else if (filterVal.startsWith('<=')) {
          const num = parseFloat(filterVal.slice(2));
          if (!isNaN(num) && statVal > num) return false;
        } else if (filterVal.startsWith('>')) {
          const num = parseFloat(filterVal.slice(1));
          if (!isNaN(num) && statVal <= num) return false;
        } else if (filterVal.startsWith('<')) {
          const num = parseFloat(filterVal.slice(1));
          if (!isNaN(num) && statVal >= num) return false;
        } else {
          const num = parseFloat(filterVal);
          if (!isNaN(num) && statVal !== num) return false;
        }
      }
      return true;
    });
  }, [personnelStatistics.employees, reportFilters]);

  // Get employee visits for the modal (uses full dataset)
  const getEmployeeVisits = useCallback((employeeId) => {
    const { filterStartDate, filterEndDate } = personnelStatistics;
    return allVisits.filter(visit => {
      const visitDate = new Date(visit.date || visit.firstVisitDate);
      return visit.employeeId === employeeId &&
             visitDate >= filterStartDate &&
             visitDate <= filterEndDate;
    }).sort((a, b) => new Date(b.date || b.firstVisitDate) - new Date(a.date || a.firstVisitDate));
  }, [allVisits, personnelStatistics]);

  // Get employee jobs for the modal (uses full dataset)
  const getEmployeeJobs = useCallback((employeeId) => {
    const { filterStartDate, filterEndDate } = personnelStatistics;
    return allCalendarJobs.filter(job => {
      const jobDate = new Date(job.startDate);
      return job.employeeId === employeeId &&
             jobDate >= filterStartDate &&
             jobDate <= filterEndDate;
    }).sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
  }, [allCalendarJobs, personnelStatistics]);

  // Handle employee row click
  const handleEmployeeClick = (stat) => {
    const employeeVisits = getEmployeeVisits(stat.employeeId);
    const employeeJobs = getEmployeeJobs(stat.employeeId);
    setSelectedEmployeeStats({
      ...stat,
      visits: employeeVisits,
      jobs: employeeJobs
    });
    setShowEmployeeModal(true);
  };

  const handleExportPersonnelReport = async () => {
    try {
      const data = filteredPersonnelEmployees;
      if (!data || data.length === 0) {
        toast.warning(text.noData);
        return;
      }

      const ExcelJS = (await import('exceljs')).default;
      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet('Personel Raporları');

      sheet.columns = [
        { header: 'Çalışan', key: 'employeeName', width: 25 },
        { header: 'Ziyaret', key: 'totalVisits', width: 12 },
        { header: 'Tamamlanan Z.', key: 'completedVisits', width: 15 },
        { header: 'İş', key: 'totalJobs', width: 12 },
        { header: 'Tamamlanan İ.', key: 'completedJobs', width: 15 },
        { header: 'Proje', key: 'projectsWorkedOn', width: 12 },
        { header: 'Başarı (%)', key: 'completionRate', width: 12 }
      ];

      // Style header row
      sheet.getRow(1).font = { bold: true };
      sheet.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF3182CE' } };
      sheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };

      data.forEach(emp => {
        const totalTasks = emp.totalVisits + emp.totalJobs;
        const completedTasks = emp.completedVisits + emp.completedJobs;
        const rate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
        sheet.addRow({
          employeeName: emp.employeeName,
          totalVisits: emp.totalVisits,
          completedVisits: emp.completedVisits,
          totalJobs: emp.totalJobs,
          completedJobs: emp.completedJobs,
          projectsWorkedOn: emp.projectsWorkedOn,
          completionRate: rate
        });
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Personel_Raporlari_${new Date().toISOString().slice(0, 10)}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);

      toast.success(text.exported);
    } catch (error) {
      console.error('Excel export error:', error);
      toast.error(text.exportError);
    }
  };

  return {
    reportPeriod,
    setReportPeriod,
    customReportStartDate,
    setCustomReportStartDate,
    customReportEndDate,
    setCustomReportEndDate,
    sortField,
    sortDirection,
    handleSort,
    reportFilters,
    handleReportFilterChange,
    personnelStatistics,
    filteredPersonnelEmployees,
    selectedEmployeeStats,
    showEmployeeModal,
    setShowEmployeeModal,
    handleEmployeeClick,
    handleExportPersonnelReport,
  };
};

export default usePersonnelStatistics;
